import { useState, useEffect, useCallback, useRef } from "react";
import {
  Download, FileJson, FileText, FileSpreadsheet, FileArchive, Loader2,
  User, FolderOpen, FileSignature, Bot, GraduationCap, Bell, Receipt, CheckCircle2, XCircle, Clock,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { logError } from "@/lib/logger";
import { PageHeader } from "@/pages/Dashboard";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const KIND_ICONS = {
  my_data: User, documents: FolderOpen, contracts: FileSignature,
  ai_conversations: Bot, certificates: GraduationCap, messages: Bell, billing: Receipt,
};
const KIND_DESC = {
  my_data: "A complete archive of your entire account",
  documents: "All uploaded files in your library",
  contracts: "Your generated & signed agreements",
  ai_conversations: "Your full AI advisor chat history",
  certificates: "Course completion certificates you've earned",
  messages: "Your notifications and message feed",
  billing: "Payment and subscription history",
};
const FORMAT_META = {
  json: { label: "JSON", icon: FileJson },
  csv: { label: "CSV", icon: FileSpreadsheet },
  pdf: { label: "PDF", icon: FileText },
  zip: { label: "ZIP", icon: FileArchive },
};

function StatusPill({ status }) {
  const map = {
    ready: { icon: CheckCircle2, cls: "text-emerald-400", label: "Ready" },
    processing: { icon: Loader2, cls: "text-[#D4AF37] animate-spin", label: "Processing" },
    failed: { icon: XCircle, cls: "text-red-400", label: "Failed" },
  };
  const m = map[status] || { icon: Clock, cls: "text-[#71717A]", label: status };
  const Icon = m.icon;
  return (
    <span className="inline-flex items-center gap-1.5 text-xs">
      <Icon className={`w-4 h-4 ${m.cls}`} /> <span className="text-[#A1A1AA]">{m.label}</span>
    </span>
  );
}

function ExportCard({ kind, label, formats, onExport, busy }) {
  const [fmt, setFmt] = useState(formats[0]);
  const Icon = KIND_ICONS[kind] || Download;
  return (
    <div className="rounded-2xl border border-white/8 bg-[#121214] p-6 flex flex-col" data-testid={`export-card-${kind}`}>
      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center shrink-0">
          <Icon className="w-5 h-5 text-[#D4AF37]" />
        </div>
        <div className="min-w-0">
          <div className="text-white font-medium">{label}</div>
          <div className="text-sm text-[#71717A] break-words">{KIND_DESC[kind]}</div>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mb-4 mt-auto">
        {formats.map((f) => {
          const Fi = FORMAT_META[f]?.icon || FileText;
          const active = fmt === f;
          return (
            <button key={f} onClick={() => setFmt(f)} data-testid={`format-${kind}-${f}`}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border transition-colors ${
                active ? "border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]" : "border-white/10 text-[#A1A1AA] hover:bg-white/5"}`}>
              <Fi className="w-3.5 h-3.5" /> {FORMAT_META[f]?.label || f.toUpperCase()}
            </button>
          );
        })}
      </div>
      <Button onClick={() => onExport(kind, fmt)} disabled={busy} data-testid={`export-btn-${kind}`}
        className="fv-gold-gradient text-[#0A0A0B] font-semibold rounded-lg hover:opacity-90 w-full">
        <Download className="w-4 h-4 mr-2" /> Export {FORMAT_META[fmt]?.label || fmt.toUpperCase()}
      </Button>
    </div>
  );
}

export function DataExportPanel() {
  const [options, setOptions] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [busy, setBusy] = useState(false);
  const pollRef = useRef(null);

  const loadJobs = useCallback(async () => {
    try {
      const { data } = await api.get("/export/jobs");
      setJobs(data);
      return data;
    } catch (err) { logError("DataExport", "Load jobs failed", err?.message); return []; }
  }, []);

  useEffect(() => {
    api.get("/export/options").then(({ data }) => setOptions(data))
      .catch((err) => logError("DataExport", "Load options failed", err?.message));
    loadJobs();
  }, [loadJobs]);

  useEffect(() => {
    const anyProcessing = jobs.some((j) => j.status === "processing");
    if (anyProcessing && !pollRef.current) {
      pollRef.current = setInterval(async () => {
        const data = await loadJobs();
        if (!data.some((j) => j.status === "processing")) {
          clearInterval(pollRef.current); pollRef.current = null;
        }
      }, 1800);
    }
    return () => { if (pollRef.current && !jobs.some((j) => j.status === "processing")) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [jobs, loadJobs]);

  const startExport = async (kind, format) => {
    setBusy(true);
    try {
      await api.post("/export", { kind, format });
      toast.success("Export started — we'll email you a secure link when it's ready.");
      await loadJobs();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Export failed to start");
    } finally { setBusy(false); }
  };

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-10">
        {options?.kinds.map((k) => (
          <ExportCard key={k.kind} kind={k.kind} label={k.label}
            formats={options.formats} onExport={startExport} busy={busy} />
        ))}
      </div>

      <div className="rounded-2xl border border-white/8 bg-[#121214] p-7" data-testid="export-jobs-section">
        <h3 className="font-heading text-xl text-white mb-4">Recent exports</h3>
        {jobs.length === 0 ? (
          <p className="text-sm text-[#71717A] py-2" data-testid="export-jobs-empty">No exports yet. Choose a data type above to get started.</p>
        ) : jobs.map((j) => (
          <div key={j.export_id} className="py-4 border-b border-white/8 last:border-0" data-testid="export-job-row">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <div className="text-white text-sm font-medium">{j.kind_label} · <span className="uppercase text-[#71717A]">{j.format}</span></div>
                <div className="text-xs text-[#71717A]">{new Date(j.created_at).toLocaleString()}{j.filename ? ` · ${j.filename}` : ""}</div>
              </div>
              <div className="flex items-center gap-4 shrink-0">
                <StatusPill status={j.status} />
                {j.status === "ready" && j.download_url && (
                  <a href={j.download_url} data-testid={`download-${j.export_id}`}
                    className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm fv-gold-gradient text-[#0A0A0B] font-semibold hover:opacity-90">
                    <Download className="w-4 h-4" /> Download
                  </a>
                )}
              </div>
            </div>
            {j.status === "processing" && (
              <div className="mt-3 h-1.5 w-full rounded-full bg-white/8 overflow-hidden" data-testid="export-progress-bar">
                <div className="h-full fv-gold-gradient transition-all duration-500" style={{ width: `${j.progress || 5}%` }} />
              </div>
            )}
            {j.status === "failed" && <p className="mt-2 text-xs text-red-400">{j.error || "Export failed. Please try again."}</p>}
          </div>
        ))}
      </div>
    </>
  );
}

export default function DataExport() {
  return (
    <div className="fv-fade-up max-w-5xl" data-testid="data-export-page">
      <PageHeader eyebrow="Account" title="Data Export"
        subtitle="Download your data anytime. Exports are delivered as secure links that expire in 24 hours." />
      <DataExportPanel />
    </div>
  );
}
